// api/lezione.ts — W4.5 Session 3 Lezione (Studia) API client.
//
// Wraps the 7 BE endpoints powering the Studia surface:
//   GET  /api/lezione/next                       — selector pick
//   GET  /api/lezione/argomento/:argomentoId     — micro-topic list + state
//   GET  /api/lezione/fast/:microTopicId         — Fast 5-section content
//   POST /api/lezione/fast/complete              — complete a Fast micro-topic
//   POST /api/lezione/deep/start                 — create or resume a Deep session
//   POST /api/lezione/deep/turn/stream           — one Prof turn (SSE per §19 R10)
//   POST /api/lezione/deep/end                   — finalize a Deep session
//
// All JWT-protected. The streaming variant of /deep/turn is the only one the
// FE chat handler uses for the Deep lesson; the JSON variant exists BE-side
// for tests + non-streaming environments.

import { apiJson, studentApiStream } from './client';

// ---------------------------------------------------------------------------
// /api/lezione/next
// ---------------------------------------------------------------------------

/** Spec §8 response shape — snake_case matching the BE contract verbatim. */
export interface LezioneNextResponseFound {
  mode: 'fast' | 'deep';
  /** For Fast: micro_topic_id; for Deep: argomento_id. */
  target_id: string;
  argomento_id: string;
  argomento_name: string;
  estimated_minutes: number;
  reason: string;
  reason_code: 'warm_walk' | 'diagnostic_seed' | 'cold_start' | 'fast_left_weak' | 'user_requested';
  deep_offered: boolean;
}

export interface LezioneNextResponseDone {
  done: true;
  message: string;
}

export type LezioneNextResponse = LezioneNextResponseFound | LezioneNextResponseDone;

// ---------------------------------------------------------------------------
// /api/lezione/studia-grid — W4.5 Session 4 §A.1 batched per-argomento state
// ---------------------------------------------------------------------------

export interface StudiaGridArgomentoRow {
  argomentoId: string;
  argomentoName: string;
  fastTotal: number;
  fastCompleted: number;
  nextFastMicroTopicId?: string | null;
  deepCompleted: boolean;
  /** True when every Fast micro-topic is taught AND a Deep is completed.
   *  Drives the §A.1 `Completato` badge. */
  completed: boolean;
  observedAnswers?: number;
  correctAnswers?: number;
  masteryScore?: number | null;
  weakPrincipleCount?: number;
  masteredPrincipleCount?: number;
}

// NOTE: This today-plan type set is mirrored verbatim in the backend at
// ai-tutor/backend/src/learning/studia-today-plan.ts — no shared FE/BE package
// exists, so the two copies are kept in sync by hand. The surface VALUES below
// are wire / navigate targets ('allena'/'chiedi' are persisted session modes) —
// never free-rename them.
export type ReadinessState =
  | 'Inizia'
  | 'Rinforza'
  | 'Ripassa'
  | 'Simula'
  | 'Quasi pronto';

export type TodayPlanSurface = 'fast_v2' | 'deep' | 'allena' | 'chiedi' | 'simula';

export interface TodayPlanAction {
  surface: TodayPlanSurface;
  argomentoId?: string;
  targetId?: string;
  label: string;
  reason: string;
  estimatedMinutes: number;
  progressCue: string;
  cta: string;
}

export interface TodayPlan {
  readinessState: ReadinessState;
  primaryAction: TodayPlanAction;
  backupAction: TodayPlanAction;
  weakTopicCue: string | null;
}

export interface StudiaGridResponse {
  rows: StudiaGridArgomentoRow[];
  todayPlan?: TodayPlan;
}

// ---------------------------------------------------------------------------
// /api/lezione/argomento/:id
// ---------------------------------------------------------------------------

export interface ArgomentoDetailMicroTopic {
  microTopicId: string;
  nameIt: string;
  sortOrder: number;
  principleIds: string[];
  fastState: 'taught' | 'untaught';
}

export interface ArgomentoDetailResponse {
  argomentoId: string;
  argomentoName: string;
  microTopics: ArgomentoDetailMicroTopic[];
  deepCompleted: boolean;
}

// ---------------------------------------------------------------------------
// /api/lezione/fast/:microTopicId
// ---------------------------------------------------------------------------

export interface FastLessonSections {
  section_1_scenario: string;
  section_2_regola: string;
  section_3_trappole: string;
  section_4_ancora: string;
}

export interface FastPracticeQuestion {
  questionId: string;
  textIt: string;
  isTrue: boolean;
  imageRef: string | null;
}

export interface FastLessonResponse {
  microTopicId: string;
  argomentoId: string;
  argomentoName: string;
  microTopicName: string;
  sections: FastLessonSections;
  practiceQuestions: FastPracticeQuestion[];
  /** Pre-Socio Session C — chat-shape Fast v2 turn 1 opener (markdown,
   *  Italian, 1-2 sentences, image-first when the micro-topic is visual via
   *  `[[image:sign:...]]` markup). NULL when migration 0015 hasn't seeded
   *  the row OR when the BE deployment predates Session C — `FastV2LezioneScreen`
   *  falls back to `sections.section_1_scenario` in either case. */
  scenarioHookMd?: string | null;
  /** Pre-Socio Session C — chat-shape Fast v2 memory anchor (markdown,
   *  Italian, 1 line, encodes the rule's key idea / Rule 13 trap baseline).
   *  NULL when un-seeded; FE falls back to composing
   *  `sections.section_2_regola` + `sections.section_3_trappole`. */
  conceptAnchorMd?: string | null;
  /** Pre-Socio Session C — chat-shape Fast v2 V/F variants for turns 4-5.
   *  Length=2 once seeded; chosen fresh by build-fast-v2-seed.mjs (NOT
   *  necessarily a subset of `practice_question_ids` v1). NULL when
   *  un-seeded; FE falls back to `practiceQuestions[0..2]` (first 2 of
   *  the v1 length=3 array). */
  practiceQuestionIdsV2?: string[] | null;
  /** Coverage-optimized Fast v2 V/F variants.
   *  Length=3 when the backend can resolve enough seed questions to recover
   *  legacy-level principle coverage; FE prefers this over the legacy length=2
   *  v2 set only when all IDs resolve in `practiceQuestions`. */
  practiceQuestionIdsV2Best?: string[] | null;
  /** Item C1 (2026-06-11) — the micro-topic's CANONICAL sign images (from its
   *  principles' `images`, signs→principles→micro-topic). The read-lesson
   *  illustration slot uses these ONLY when no practice-question imageRef exists,
   *  so a sign-bearing topic shows a real sign instead of the dashed placeholder.
   *  Capped at 2; relative asset paths (bare "54.png" → /img_sign/54.png, or a
   *  full "img_sign_by_hash/..." path). Empty/absent when the topic has no signs
   *  (or for a BE deployment predating this field — the FE then keeps the
   *  placeholder, no regression). */
  signImageRefs?: string[] | null;
}

// ---------------------------------------------------------------------------
// /api/lezione/fast/complete
// ---------------------------------------------------------------------------

/**
 * Pre-Socio Session C — Fast v1 vs v2 lesson variant discriminator.
 *
 * - `'fast'` (default — back-compat for pre-existing JWTs): legacy 5-section
 *   card flow with exactly 3 V/F outcomes per spec §19 R14.
 * - `'fast_v2'`: chat-shape flow per spec §6.1 — 2 or 3 compact V/F
 *   outcomes. Requires `attemptSessionId` for the §6.2 dedup gate.
 */
export type FastLessonVariant = 'fast' | 'fast_v2';

export interface FastCompleteRequest {
  microTopicId: string;
  /** Optional — defaults to 'fast' (v1) for back-compat with pre-existing
   *  JWTs. Pass 'fast_v2' to opt into the chat-shape contract (length=2/3 +
   *  attemptSessionId required). */
  lessonVariant?: FastLessonVariant;
  /** Pre-Socio Session C §6.2 — REQUIRED when `lessonVariant='fast_v2'`. The
   *  FE generates a stable UUID once per Fast v2 attempt and includes it on
   *  every retry; the BE's first-write-wins dedup gate uses the
   *  `(student_id, micro_topic_id, attempt_session_id)` tuple to short-
   *  circuit duplicates without double-crediting mastery + XP. */
  attemptSessionId?: string;
  /** v1: must be length=3. v2: must be length=2 or length=3. The BE validates both. */
  vfResults: Array<{
    questionId: string;
    correct: boolean;
    responseTimeMs: number;
  }>;
}

export interface FastCompleteResponse {
  ok: true;
  sessionId: string | null;
  principlesCovered: string[];
  /** Pre-Socio Session C §6.2 — true when the BE's dedup gate hit on a v2
   *  retry (same `attemptSessionId`). FE treats this as "lesson already
   *  recorded; do not double-bump XP/mastery client-side". Absent on v1
   *  responses + on a fresh v2 first-write. */
  deduplicated?: boolean;
}

// ---------------------------------------------------------------------------
// /api/lezione/fast/v2/teach/stream
// ---------------------------------------------------------------------------

export interface FastV2TeachStreamRequest {
  microTopicId: string;
  studentReply: string;
  attemptSessionId: string;
}

export interface FastV2TeachDonePayload {
  fallback: boolean;
  reason?: string;
}

export interface FastV2TeachStreamEvents {
  onBubble: (chunk: string) => void;
  onFallback?: (reason?: string) => void;
  onDone: (payload: FastV2TeachDonePayload) => void;
  onError?: (error: string) => void;
}

// ---------------------------------------------------------------------------
// /api/lezione/guided/:argomentoId — FP5 guided-lesson reader content
// ---------------------------------------------------------------------------
//
// The baked GuidedLesson document (one per argomento, 25 total; migrations
// 0025/0026). Flag-gated server-side by `guided_lesson_serve_enabled` (404 when
// OFF). The shape mirrors the BE contract at backend/src/gateway/guided-lesson.ts
// verbatim — no shared FE/BE package, so kept in sync by hand (same discipline
// as the StudiaGrid types above).

/** A single woven check inside a chapter. */
export interface GuidedLessonQuiz {
  /** 'vf' = Vero/Falso (options = ['Vero','Falso']); 'mc' = multiple choice. */
  type: 'vf' | 'mc';
  prompt_md: string;
  options: string[];
  /** Index into `options` of the correct answer. */
  correct_index: number;
  /** The "perché" — why the correct answer is correct. */
  why_md: string;
  /** The repair line shown on a wrong answer. */
  repair_md: string;
  source_subsection?: string;
  practice_question_id?: string;
}

export type GuidedLessonSegment =
  | {
      kind: 'block';
      role: 'headline' | 'scenario' | 'principle' | 'angle' | 'worked_example' | 'reflection' | 'note';
      label?: string;
      /** Primary scenario image, by asset ref only (e.g. '615.png'). */
      image_ref?: string;
      alt_image_refs?: string[];
      body_md: string;
    }
  | { kind: 'quiz'; quiz: GuidedLessonQuiz };

export interface GuidedLessonChapter {
  /** 1-based position in the projected lesson. */
  order: number;
  /** Original chapter order in the argomento-wide authored lesson. */
  source_chapter_order?: number;
  /** 1-based order inside the projected micro-topic lesson. */
  display_chapter_order?: number;
  title: string;
  principle_ids: string[];
  micro_topic_ids: string[];
  segments: GuidedLessonSegment[];
  canonicalUnitId?: string | null;
  canonicalUnitMappingStatus?: 'mapped' | 'split_needed' | 'merge_needed' | 'gap';
  canonicalUnitBlockerType?: string | null;
  canonicalUnitCandidateIds?: string[];
  canonicalLessonUnits?: Array<{
    canonicalLessonUnitId: string;
    canonicalUnitId: string | null;
    canonicalUnitOrder: number;
    displayChapterOrder: number;
    sourceChapterOrder: number;
    appendiceSubsectionId: string | null;
    appendiceTitle: string | null;
    mappingStatus: 'mapped' | 'split_needed' | 'merge_needed' | 'gap';
    blockerType: string | null;
    lessonImageRefs: string[];
    fullImageRefs: string[];
    principleIds: string[];
    sourceSegmentIndexes: number[];
    quizIndexes: number[];
    assignmentRule: string;
  }>;
  /** Student-facing images owned by the canonical chapter/subsection unit. */
  lessonImageRefs?: string[];
  lessonImages?: Array<{ ref: string; name: string | null }>;
  fullImageRefs?: string[];
  /** Compatibility alias for older code; prefer lessonImageRefs. */
  signImageRefs?: string[];
  /** Compatibility alias for older code; prefer lessonImages. */
  signImages?: Array<{ ref: string; name: string | null }>;
}

export interface GuidedLessonEscape {
  chapter_order: number;
  anchor_label: string;
}

export interface GuidedLessonResponse {
  argomento_id: string;
  argomento_name?: string;
  lesson_id?: string;
  lesson_title?: string;
  principle_ids_covered: string[];
  chapter_count: number;
  quiz_count: number;
  chapters: GuidedLessonChapter[];
  escapes: GuidedLessonEscape[];
  generated_by: string;
  generated_at?: string | number | null;
  source_asset_hash?: string;
}

export interface GuidedQuizAnswerRequest {
  sessionId: string;
  argomentoId: string;
  chapterOrder: number;
  quizIndex: number;
  selectedIndex: number;
  responseTimeMs: number;
}

export interface GuidedQuizAnswerResponse {
  ok: true;
  deduplicated?: boolean;
  updates: Array<{
    principleId: string;
    encountersAfter: number;
    masteryScoreAfter: number;
    sourceOfWeakness: string | null;
  }>;
}

export interface GuidedLessonAttempt {
  id: string;
  sessionId: string;
  argomentoId: string;
  lessonId: string;
  status: 'in_progress' | 'completed' | 'abandoned';
  currentSourceChapterOrder: number;
  startedAt: string;
  completedAt: string | null;
  contentVersion: string | null;
  sourceAssetHash: string | null;
}

export interface GuidedLessonAttemptAnswer {
  attempt_id: string;
  source_chapter_order: number;
  display_chapter_order: number;
  quiz_index: number;
  selected_index: number;
  correct_index: number;
  is_correct: boolean;
  response_time_ms?: number | null;
  question_key: string;
  principle_ids: string[];
  answered_at?: string;
  feedback?: { whyMd: string; repairMd: string } | null;
  mastery_status?: 'committed' | 'legacy_unverified';
}

export interface GuidedLessonAttemptStartResponse {
  attempt: GuidedLessonAttempt;
  lesson: GuidedLessonResponse;
  answers: GuidedLessonAttemptAnswer[];
}

export interface GuidedLessonAttemptAnswerRequest {
  sourceChapterOrder: number;
  displayChapterOrder: number;
  quizIndex: number;
  selectedIndex: number;
  responseTimeMs: number;
}

export interface GuidedLessonAttemptAnswerResponse {
  ok: true;
  answer: GuidedLessonAttemptAnswer & { isCorrect?: boolean };
  mastery_status: 'committed' | 'legacy_unverified';
  replayed: boolean;
  feedback: {
    whyMd: string;
    repairMd: string;
  } | null;
}

export interface GuidedLessonAttemptCompleteResponse {
  attempt: GuidedLessonAttempt;
  summary: {
    answeredCount: number;
    correctCount: number;
    accuracyPct: number;
  };
}

export interface CanonicalLessonLatestAttemptSummary {
  attemptId: string;
  completedAt: string | null;
  answeredCount: number;
  correctCount: number;
  accuracyPct: number;
}

export interface CanonicalLessonProgressLesson {
  argomentoId: string;
  lessonId: string;
  status: 'not_started' | 'in_progress' | 'completed';
  activeAttemptId: string | null;
  currentSourceChapterOrder: number | null;
  latestAttempts: CanonicalLessonLatestAttemptSummary[];
}

export interface CanonicalLessonProgressArgomento {
  argomentoId: string;
  completedLessons: number;
  totalLessons: number;
  completionPct: number;
  quizAnswered: number;
  correctAnswers: number;
  correctRatePct: number;
}

export interface CanonicalLessonProgressSummary {
  contractVersion: 2;
  totals: {
    completedLessons: number;
    totalLessons: number;
    completionPct: number;
    quizAnswered: number;
    correctAnswers: number;
    correctRatePct: number;
  };
  argomenti: CanonicalLessonProgressArgomento[];
  lessons: CanonicalLessonProgressLesson[];
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isNonNegativeInteger(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value) && Number.isInteger(value) && value >= 0;
}

function isPercentage(value: unknown): value is number {
  return isNonNegativeInteger(value) && value <= 100;
}

function isNullableString(value: unknown): value is string | null {
  return value === null || typeof value === 'string';
}

function isTotalsV2(value: unknown): boolean {
  if (!isRecord(value) || Object.hasOwn(value, 'preparationIndex')) return false;
  return isNonNegativeInteger(value.completedLessons)
    && isNonNegativeInteger(value.totalLessons)
    && value.completedLessons <= value.totalLessons
    && isPercentage(value.completionPct)
    && isNonNegativeInteger(value.quizAnswered)
    && isNonNegativeInteger(value.correctAnswers)
    && value.correctAnswers <= value.quizAnswered
    && isPercentage(value.correctRatePct);
}

function isArgomentoV2(value: unknown): boolean {
  if (!isRecord(value) || Object.hasOwn(value, 'status')) return false;
  return typeof value.argomentoId === 'string'
    && value.argomentoId.length > 0
    && isNonNegativeInteger(value.completedLessons)
    && isNonNegativeInteger(value.totalLessons)
    && value.completedLessons <= value.totalLessons
    && isPercentage(value.completionPct)
    && isNonNegativeInteger(value.quizAnswered)
    && isNonNegativeInteger(value.correctAnswers)
    && value.correctAnswers <= value.quizAnswered
    && isPercentage(value.correctRatePct);
}

function isLatestAttemptV2(value: unknown): boolean {
  if (!isRecord(value)) return false;
  return typeof value.attemptId === 'string'
    && value.attemptId.length > 0
    && isNullableString(value.completedAt)
    && isNonNegativeInteger(value.answeredCount)
    && isNonNegativeInteger(value.correctCount)
    && value.correctCount <= value.answeredCount
    && isPercentage(value.accuracyPct);
}

function isLessonV2(value: unknown): boolean {
  if (!isRecord(value)) return false;
  return typeof value.argomentoId === 'string'
    && value.argomentoId.length > 0
    && typeof value.lessonId === 'string'
    && value.lessonId.length > 0
    && (value.status === 'not_started' || value.status === 'in_progress' || value.status === 'completed')
    && isNullableString(value.activeAttemptId)
    && (value.currentSourceChapterOrder === null || isNonNegativeInteger(value.currentSourceChapterOrder))
    && Array.isArray(value.latestAttempts)
    && value.latestAttempts.every(isLatestAttemptV2);
}

export function parseCanonicalLessonProgressSummary(
  value: unknown,
): CanonicalLessonProgressSummary {
  if (
    !isRecord(value)
    || value.contractVersion !== 2
    || !isTotalsV2(value.totals)
    || !Array.isArray(value.argomenti)
    || !value.argomenti.every(isArgomentoV2)
    || !Array.isArray(value.lessons)
    || !value.lessons.every(isLessonV2)
  ) {
    throw new Error('invalid_lesson_progress_contract_v2');
  }
  return value as unknown as CanonicalLessonProgressSummary;
}

// ---------------------------------------------------------------------------
// /api/lezione/deep/start
// ---------------------------------------------------------------------------

export interface DeepStartRequest {
  argomentoId: string;
  forceRestart?: boolean;
}

/** A transcript turn as persisted in `lezione_session.transcript`. */
export interface DeepTurnRecord {
  role: 'user' | 'assistant' | 'system_injected';
  content: string;
  ts: string;
  state?: { covered: string[]; checked: boolean; finished: boolean };
  vf_checkpoint?: { question_id: string; answer: 'V' | 'F'; correct: boolean };
}

export interface DeepStartResponse {
  sessionId: string;
  isResume: boolean;
  /** Human argomento name (e.g., 'Pannelli integrativi'). The FE renders it in
   *  the §A.4 banner / resume modal / carta finale so the slug `arg5` never
   *  leaks into student-facing copy. Echoed by the BE on both branches. */
  argomento_name: string;
  opener: string;
  principlesCovered: string[];
  transcriptSoFar: DeepTurnRecord[];
}

// ---------------------------------------------------------------------------
// /api/lezione/deep/turn/stream — SSE per §19 R10
// ---------------------------------------------------------------------------
//
// Event order:
//   1. `bubble`        token chunks, text-only, <state> tag stripped
//   2. `state`         single emit, parsed {covered, checked, finished}
//   3. `vf_checkpoint` single emit IF state.checked === true
//   4. `done`          final summary

export interface DeepTurnStreamRequest {
  sessionId: string;
  studentMessage: string;
  checkpoint_answer?: { question_id: string; answer: 'V' | 'F' } | null;
}

export interface DeepTurnState {
  covered: string[];
  checked: boolean;
  finished: boolean;
  /**
   * Pre-Socio Session B §5.2 — when the Prof has offered a scenario puzzle,
   * the question_id is captured here. The V/F end checkpoint is BLOCKED
   * (BE-side) until cleared by the next turn (Prof reacts to the student's
   * free-text scenario answer). FE renders the question's image prominently
   * and switches the composer prompt to "Cosa noti?".
   */
  scenarioOffered?: string | null;
  /**
   * Pre-Socio Session B §5.6 — when `scenarioOffered` is set, the BE looks
   * up `questions.image_ref` and surfaces the canonical PNG path so the FE
   * renders a real `<img>` (not the localized "[immagine domanda non
   * disponibile]" placeholder). Null when no scenario is offered or the
   * lookup missed.
   */
  scenarioOfferedImageRef?: string | null;
  /**
   * Pre-Socio Session B §5.3 — argomento ids briefly invoked during the
   * lesson via cross-argomento deviations. Soft "encountered" flag, NOT a
   * fresh `taught_deep_at`. De-duped across the session transcript.
   */
  secondaryArgomenti?: string[];
  /**
   * Pre-Socio Session B §5.5 — question_ids of scenarios shown mid-lesson
   * (non-mastery checks; conversational, not exam-shaped). De-duped.
   */
  midLessonScenarios?: string[];
  /**
   * Pre-Socio Session B §5.6 — question_id when the lesson opened with a
   * scenario puzzle. First-write-wins across the session. Null when the
   * lesson did not open with a scenario.
   */
  scenarioFirstOpenerQuestionId?: string | null;
  /**
   * Pre-Socio Session B §5.6 — when `scenarioFirstOpenerQuestionId` is set,
   * the BE looks up `questions.image_ref` and surfaces the canonical PNG
   * path so the FE renders the opener image inline in the very first
   * assistant turn. Null when the lesson did not open with a scenario or
   * the lookup missed.
   */
  scenarioFirstOpenerImageRef?: string | null;
}

export interface DeepTurnVfCheckpoint {
  questionId: string;
  statement: string;
}

export interface DeepTurnDonePayload {
  finished: boolean;
  autoFinishedSoftCap?: boolean;
  checkpointResolved?: { sessionId: string; principleId: string; correct: boolean };
  principlesCovered: string[];
}

export interface DeepTurnStreamEvents {
  onBubble: (chunk: string) => void;
  onState: (state: DeepTurnState) => void;
  onCheckpoint?: (cp: DeepTurnVfCheckpoint) => void;
  onDone: (payload: DeepTurnDonePayload) => void;
  onError?: (error: string) => void;
}

// ---------------------------------------------------------------------------
// /api/lezione/deep/end
// ---------------------------------------------------------------------------

export interface DeepEndRequest {
  sessionId: string;
  reason: 'finished' | 'abandoned';
}

export interface DeepEndResponse {
  ok: true;
  brainPath: 'sync' | 'batch' | 'skipped' | 'already_summarized';
  highSignal: boolean;
}

// ---------------------------------------------------------------------------
// Client surface
// ---------------------------------------------------------------------------

export const lezioneApi = {
  async next(): Promise<LezioneNextResponse> {
    return apiJson<LezioneNextResponse>('/api/lezione/next', { method: 'GET' });
  },

  async argomentoDetail(argomentoId: string): Promise<ArgomentoDetailResponse> {
    return apiJson<ArgomentoDetailResponse>(
      `/api/lezione/argomento/${encodeURIComponent(argomentoId)}`,
      { method: 'GET' },
    );
  },

  async studiaGrid(): Promise<StudiaGridResponse> {
    return apiJson<StudiaGridResponse>('/api/lezione/studia-grid', { method: 'GET' });
  },

  /** FP5 — fetch the baked guided lesson for an argomento. Server-side
   *  flag-gated (`guided_lesson_serve_enabled`); a 404 means the lessons are
   *  not yet live (the FE entry point should already be hidden by the flag). */
  async guidedLesson(argomentoId: string, lessonId?: string): Promise<GuidedLessonResponse> {
    const path = lessonId
      ? `/api/lezione/guided/${encodeURIComponent(argomentoId)}/${encodeURIComponent(lessonId)}`
      : `/api/lezione/guided/${encodeURIComponent(argomentoId)}`;
    return apiJson<GuidedLessonResponse>(
      path,
      { method: 'GET' },
    );
  },

  async guidedAttemptStart(req: {
    argomentoId: string;
    lessonId: string;
    restart?: boolean;
  }): Promise<GuidedLessonAttemptStartResponse> {
    return apiJson<GuidedLessonAttemptStartResponse>('/api/lezione/guided/attempts/start', {
      method: 'POST',
      body: JSON.stringify(req),
    });
  },

  async guidedAttemptAnswer(
    attemptId: string,
    req: GuidedLessonAttemptAnswerRequest,
  ): Promise<GuidedLessonAttemptAnswerResponse> {
    return apiJson<GuidedLessonAttemptAnswerResponse>(
      `/api/lezione/guided/attempts/${encodeURIComponent(attemptId)}/answers`,
      {
        method: 'POST',
        body: JSON.stringify(req),
      },
    );
  },

  async guidedAttemptComplete(attemptId: string): Promise<GuidedLessonAttemptCompleteResponse> {
    return apiJson<GuidedLessonAttemptCompleteResponse>(
      `/api/lezione/guided/attempts/${encodeURIComponent(attemptId)}/complete`,
      { method: 'POST' },
    );
  },

  async lessonProgressSummary(): Promise<CanonicalLessonProgressSummary> {
    const payload = await apiJson<unknown>('/api/progress/lessons-summary?contract=2', {
      method: 'GET',
    });
    return parseCanonicalLessonProgressSummary(payload);
  },

  async guidedQuizAnswer(req: GuidedQuizAnswerRequest): Promise<GuidedQuizAnswerResponse> {
    return apiJson<GuidedQuizAnswerResponse>('/api/lezione/guided/quiz-answer', {
      method: 'POST',
      body: JSON.stringify(req),
    });
  },

  async fastLesson(microTopicId: string): Promise<FastLessonResponse> {
    return apiJson<FastLessonResponse>(
      `/api/lezione/fast/${encodeURIComponent(microTopicId)}`,
      { method: 'GET' },
    );
  },

  /**
   * V28 round-2 R2-12 + #65 "letta" — mark a PURE READ lesson micro-topic as
   * read (completed reading). Stamps `taught_fast_at` BE-side with ZERO mastery
   * and ZERO XP (it is the light read-marker, SEPARATE from the colored
   * mastery/preparation bar). Idempotent: re-calling once stamped is a no-op.
   *
   * Fire-and-forget at the call site (errors swallowed; never blocks the
   * forward navigation that completes the lesson).
   */
  async fastMarkRead(microTopicId: string): Promise<{ ok: true }> {
    return apiJson<{ ok: true }>('/api/lezione/fast/mark-read', {
      method: 'POST',
      body: JSON.stringify({ microTopicId }),
    });
  },

  async fastComplete(req: FastCompleteRequest): Promise<FastCompleteResponse> {
    // Pre-Socio Session C: forward optional `lessonVariant` + `attemptSessionId`
    // ONLY when present so legacy v1 callers continue to send the exact same
    // wire shape (no surprise field surfacing on the BE for pre-existing
    // pre-existing JWTs that omit the discriminator).
    const body: Record<string, unknown> = {
      microTopicId: req.microTopicId,
      vfResults: req.vfResults,
    };
    if (req.lessonVariant) body.lessonVariant = req.lessonVariant;
    if (req.attemptSessionId) body.attemptSessionId = req.attemptSessionId;
    return apiJson<FastCompleteResponse>('/api/lezione/fast/complete', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  },

  async fastV2TeachStream(
    req: FastV2TeachStreamRequest,
    events: FastV2TeachStreamEvents,
    signal?: AbortSignal,
  ): Promise<void> {
    const init = {
      method: 'POST' as const,
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(req),
      signal,
    };
    for await (const msg of studentApiStream('/api/lezione/fast/v2/teach/stream', init)) {
      switch (msg.event) {
        case 'bubble':
          events.onBubble((msg.data as { text?: string }).text ?? '');
          break;
        case 'fallback':
          events.onFallback?.((msg.data as { reason?: string }).reason);
          break;
        case 'done':
          events.onDone(msg.data as FastV2TeachDonePayload);
          return;
        case 'error':
          events.onError?.((msg.data as { error?: string }).error ?? 'unknown');
          return;
      }
    }
  },

  async deepStart(req: DeepStartRequest): Promise<DeepStartResponse> {
    return apiJson<DeepStartResponse>('/api/lezione/deep/start', {
      method: 'POST',
      body: JSON.stringify(req),
    });
  },

  async deepEnd(req: DeepEndRequest): Promise<DeepEndResponse> {
    return apiJson<DeepEndResponse>('/api/lezione/deep/end', {
      method: 'POST',
      body: JSON.stringify(req),
    });
  },

  /** Streams a Deep turn per §19 R10. Resolves once the SSE `done` (or
   *  `error`) event has been delivered. */
  async deepTurnStream(
    req: DeepTurnStreamRequest,
    events: DeepTurnStreamEvents,
    signal?: AbortSignal,
  ): Promise<void> {
    const init = {
      method: 'POST' as const,
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(req),
      signal,
    };
    for await (const msg of studentApiStream('/api/lezione/deep/turn/stream', init)) {
      switch (msg.event) {
        case 'bubble': {
          // §19 R10 client-strip safety (fix-pass): the BE already strips
          // `<state>...</state>` blocks before emitting bubble chunks, but a
          // belt-and-suspenders client-side strip catches any future BE
          // regression that lets a tag leak into a token chunk.
          const text = (msg.data as { text: string }).text || '';
          events.onBubble(text.replace(/<state>[\s\S]*?<\/state>/g, ''));
          break;
        }
        case 'state':
          events.onState(msg.data as DeepTurnState);
          break;
        case 'vf_checkpoint':
          events.onCheckpoint?.(msg.data as DeepTurnVfCheckpoint);
          break;
        case 'done':
          events.onDone(msg.data as DeepTurnDonePayload);
          return;
        case 'error':
          events.onError?.((msg.data as { error?: string }).error ?? 'unknown');
          return;
      }
    }
  },
};
